import React, { useState } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaArrowRight, FaBullseye, FaLightbulb, FaUsers, FaRocket } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import './About.css';

const HoverCard = ({ image, children, className, icon: Icon }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className={`hover-card-custom position-relative overflow-hidden rounded-4 shadow-sm h-100 ${className}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ minHeight: 320 }}
    >
      <AnimatePresence mode="wait">
        {!hovered ? (
          <motion.div
            key="content"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="p-4 d-flex flex-column h-100"
          >
            <div className="icon-circle mb-3">
              <Icon size={24} />
            </div>
            {children}
          </motion.div>
        ) : (
          <motion.div
            key="image"
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="position-absolute top-0 start-0 w-100 h-100"
            style={{
              backgroundImage: `url(/${image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            <div className="card-overlay w-100 h-100 d-flex align-items-center justify-content-center">
              <motion.div 
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                className="arrow-circle"
              >
                <FaArrowRight size={24} color="#fff" />
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const About = () => {
  const { t } = useTranslation();

  const renderTranslatedHTML = (key) => (
    <span dangerouslySetInnerHTML={{ __html: t(key) }} />
  );

  return (
    <div className="about-page bg-white">
      <section className="about-hero py-5 mb-5">
        <Container>
          <Row>
            <Col lg={9}>
                <h1 className="display-4 fw-bold mb-3" style={{ color: '#006186' }}>
                  {t('about_hero_title', 'Об ассоциации выпускников ОшТУ')}
                </h1>
              <div className="header-line mb-4" style={{ color: '#006186' }}></div>
                <p className="lead text-muted mx-auto">
                  {t('about_hero_subtitle')}
                </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* SECTION 2: VISION & MISSION */}
      <Container className="pb-5">
        <Row className="g-4 mb-5">
          <Col md={6}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="h-100"
            >
              <HoverCard image="1.jpg" icon={FaLightbulb} className="bg-light-blue">
                <h3 className="h4 fw-bold color-primary">
                  {renderTranslatedHTML('about_vision_title')}
                </h3>
                <p className="text-secondary mt-3 lh-lg">
                  {renderTranslatedHTML('about_vision_text')}
                </p>
              </HoverCard>
            </motion.div>
          </Col>

          <Col md={6}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="h-100"
            >
              <HoverCard image="2.jpg" icon={FaBullseye} className="bg-light-yellow">
                <h3 className="h4 fw-bold color-warning">
                  {renderTranslatedHTML('about_mission_title')}
                </h3>
                <p className="text-secondary mt-3 lh-lg">
                  {renderTranslatedHTML('about_mission_text')}
                </p>
              </HoverCard>
            </motion.div>
          </Col>
        </Row>

        {/* SECTION 3: FEATURES & COMMUNITY */}
        <Row className="g-4 mb-5 pt-lg-4">
          <Col md={6}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <HoverCard image="3.jpg" icon={FaRocket} className="bg-light-green">
                <h3 className="h4 fw-bold color-success">
                  {renderTranslatedHTML('about_features_title')}
                </h3>
                <ul className="custom-list mt-3">
                  {[1, 2, 3, 4, 5].map((item) => (
                    <li key={item} className="mb-2">
                      <span className="bullet"></span>
                      {t(`about_features_list${item}`)}
                    </li>
                  ))}
                </ul>
              </HoverCard>
            </motion.div>
          </Col>

          <Col md={6}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <HoverCard image="4.jpg" icon={FaUsers} className="bg-light-red">
                <h3 className="h4 fw-bold color-danger">
                  {renderTranslatedHTML('about_community_title')}
                </h3>
                <ul className="custom-list mt-3">
                  {[1, 2, 3, 4].map((item) => (
                    <li key={item} className="mb-2">
                      <span className="bullet"></span>
                      {t(`about_community_list${item}`)}
                    </li>
                  ))}
                </ul>
              </HoverCard>
            </motion.div>
          </Col>
        </Row>

      </Container>
    </div>
  );
};

export default About;